import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useAuthContext } from '../../../context/auth.context'
import { lmsAdmin } from '../api'
import '../lms-admin.scss'
import { BrandingDisplayCard } from './branding-display-card'
import { LmsIcon } from '../lms-icon'
import { LmsTrail, lmsTrail } from '../lms-trail'

// Customer Settings (docs/08 §11, design edxr-customer-settings-v1.0.html).
// v1 is largely informational: the customer record is edited in the main
// org settings; a "default LMS connection" cannot apply in v1 because
// every SCORM launcher is built for a specific connection; linking policy
// and webhooks are explicitly v2 (deviations D-048).
// The Branding & Identity card returned with the 2026-06-11 launcher
// dynamic branding spec (deliberate D-048 reversal): see
// settings/branding-display-card.js.

export const ViewCustomerSettings = () => {
  const history = useHistory()
  const authContext = useAuthContext()

  const [connections, updateConnections] = useState(null)

  useEffect(() => {
    lmsAdmin
      .listConnections()
      .then(result => updateConnections((result.connections || []).filter(c => c.status === 'active')))
      .catch(() => updateConnections([]))
  }, [])

  const customer = authContext.state.organizationName

  const card = (icon, title, children, testid) => (
    <div className="lms-card" style={{ marginBottom: 14 }} data-testid={testid}>
      <div className="lms-card-head">
        <div className="icon">
          <LmsIcon name={icon} />
        </div>
        <div className="body">
          <h2>{title}</h2>
        </div>
      </div>
      <div style={{ padding: '14px 18px', fontSize: 13 }}>{children}</div>
    </div>
  )

  return (
    <div className="lms-admin" data-testid="lms-customer-settings">
      <LmsTrail trail={lmsTrail({ url: '/org/lms/settings', text: 'Settings' })} />
      <div className="page-header">
        <div className="page-header-text">
          <h1 className="page-header-title">
            LMS Settings
            <span className="page-header-customer">
              <LmsIcon name="building" />
              {customer}
            </span>
          </h1>
          <p className="page-header-sub">Settings for your organization's LMS connections.</p>
        </div>
      </div>

      {card(
        'building',
        'Organization',
        <React.Fragment>
          <dl className="kv-grid">
            <dt>Name</dt>
            <dd>{customer}</dd>
          </dl>
          <div className="field-help" style={{ marginTop: 8 }}>
            Your organization's name and logo are edited in the main organization settings, not here.
          </div>
        </React.Fragment>,
        'card-customer'
      )}

      <BrandingDisplayCard />

      {card(
        'plug',
        'Default LMS connection',
        <React.Fragment>
          <p style={{ margin: 0 }}>
            There is no default connection: every launcher is built for a specific connection, so a launch always knows
            its connection. {connections === null ? '' : `${connections.length} active connection${connections.length === 1 ? '' : 's'}.`}
          </p>
          <button
            className="btn btn-light border btn-sm"
            type="button"
            style={{ marginTop: 10 }}
            onClick={() => history.push('/org/lms/connections')}
          >
            Manage connections <LmsIcon name="arrow-right" className="ms-1" style={{ fontSize: 9 }} />
          </button>
        </React.Fragment>,
        'card-default-connection'
      )}

      {card(
        'users',
        'Admin users & roles',
        <React.Fragment>
          <p style={{ margin: 0 }}>
            LMS admin access rides on the existing user management — grant the LMS role to org users there.
          </p>
          <button
            className="btn btn-light border btn-sm"
            type="button"
            style={{ marginTop: 10 }}
            data-testid="users-link"
            onClick={() => history.push('/org/user/list')}
          >
            Manage users <LmsIcon name="arrow-right" className="ms-1" style={{ fontSize: 9 }} />
          </button>
        </React.Fragment>,
        'card-users'
      )}

      {card(
        'id-badge',
        'Linking policy',
        <p style={{ margin: 0 }}>
          People are linked automatically the first time they launch a course from your LMS.
          <span className="pill neutral" style={{ marginLeft: 8 }}>
            <span className="dot" />
            Approval before linking: not available yet
          </span>
        </p>,
        'card-linking-policy'
      )}

      {card(
        'tower-broadcast',
        'Webhooks',
        <p style={{ margin: 0 }}>
          <span className="pill neutral" style={{ marginRight: 8 }}>
            <span className="dot" />
            Not available yet
          </span>
          Session events are listed under LMS &rsaquo; Activity.
        </p>,
        'card-webhooks'
      )}
    </div>
  )
}
